// =============================================================================
// ui/tabs/PropEconomics.tsx — the Prop Report's Economics tab.
// -----------------------------------------------------------------------------
// Real cash in against real cash out. Transcribed from Josh's screenshots, and
// COMPLETE: Lifetime Cash Position · Eval Pipeline · Rebill Risk (conditional) ·
// Firm Comparison · Payout Velocity.
//
// ⚠ PAYOUT VELOCITY IS THE ONE CARD HERE NEVER SEEN ON REAL DATA. Josh has zero
// payout adjustments, so only its empty state has ever rendered against his
// book; the cadence, projection, ratio and sparkline are covered by fixtures
// checked against beta's source. That is weaker evidence than the rest of this
// tab and the tests say so too.
//
// ── ⚠ LIFETIME, NEVER WINDOWED ──
//
// The copy says why in beta's own words: "a date filter would let you pick the
// window where you happened to be up". There is no period chip on this tab and
// the global one does not reach it.
//
// ── 🚨 ITS POPULATION IS NOT HEALTH'S (D77) ──
//
// Health buckets with `classifyPropAccount`, where STATUS beats TYPE, so a blown
// funded account is `blown` and contributes no cushion. Economics buckets on TYPE
// ALONE, because a blown funded account still cost what it cost and still counts
// as a funded account reached.
//
// On Josh's book the two disagree out loud — Health reads `0 funded`, Economics
// reads `2 funded` — and both are right about their own question. That is the
// clearest live example of why D77 forbids a shared population helper.
//
// ── SIGNS ──
//
// Beta runs every figure here through one signed formatter, so its Fees Paid cell
// reads `−+$310` and its firm table shows `+$76` of SPEND. Costs and receipts are
// magnitudes and carry no sign here; only `netCash` and `net` are results, and
// they keep theirs. Same call as the Health tab's, and flagged the same way.
// =============================================================================

import { useMemo } from 'react';
import {
  computeEconomics, economicsByFirm, economicsRebillRisk, payoutVelocity,
  type EconomicsAccount, type FirmRow, type PayoutVelocity, type RebillRisk,
} from '../../selectors/economics';
import type { PropRow } from '../../io/sync/propUniverse';
import type { WholeBook } from '../../selectors/population';
import { money, moneyPlain, num, pct } from '../format';

export interface EconomicsViewProps {
  accounts: readonly EconomicsAccount[];
  /** Null until the whole book is proven — the tab blocks rather than caveats. */
  population: WholeBook<PropRow> | null;
  today: string | null;
}

export function EconomicsView({ accounts, population, today }: EconomicsViewProps) {
  const e = useMemo(
    () => (population ? computeEconomics(accounts, population) : null),
    [accounts, population],
  );
  const firms = useMemo(() => (e ? economicsByFirm(e) : []), [e]);
  const rebill = useMemo(
    () => (e && population && today ? economicsRebillRisk(e, population, today) : []),
    [e, population, today],
  );

  // ⚠ SAME YTD WINDOW AS THE REST OF THE TAB — from the range ANCHOR, never the
  // date chip. A payout cadence that changes because a filter above says "This
  // week" is not a cadence.
  const velocity = useMemo(
    () => (today ? payoutVelocity(accounts, `${today.slice(0, 4)}-01-01`, today) : null),
    [accounts, today],
  );

  if (!e || !velocity) return <p className="muted">Loading your whole trade history…</p>;

  if (!e.accounts.length) {
    return (
      <section className="card">
        <h3>No cost-bearing accounts</h3>
        <p className="muted">
          Economics counts evaluation and funded accounts only — a personal brokerage
          account has no eval fee and no payout, and SIM or Replay accounts cost nothing
          real. Add a prop account in <b>Accounts</b> to populate this view.
        </p>
      </section>
    );
  }

  const p = e.pipeline;

  return (
    <>
      <section className="card pr-card">
        <h3>Lifetime Cash Position</h3>
        <p className="muted">
          Every fee you have paid against every payout you have received. Lifetime on
          purpose — a date filter would let you pick the window where you happened to be up.
        </p>
        {/* ⚠ SIGNED, and the only hero figure on the tab. This is a RESULT and
            its sign is the entire message. */}
        <div className={`pr-hero ${e.netCash >= 0 ? 'green' : 'red'}`}>{money(e.netCash, 0)}</div>
        <div className="muted pr-hero-sub">
          {moneyPlain(e.payouts, 0)} received · {moneyPlain(e.fees, 0)} paid · across{' '}
          {e.accounts.length} account{e.accounts.length === 1 ? '' : 's'}
        </div>

        <div className="pr-cells">
          {/* ⚠ NO SUB. This tile carried "Cash the firms have actually sent
              you" — invented here, not ported; beta passes three arguments and
              its tile renders no sub line at all. Caught by a side-by-side
              screenshot, like everything else on this list. */}
          <Cell
            label="Payouts received" value={moneyPlain(e.payouts, 0)} cls="green"
            sub=""
          />
          {/* ⚠ `red`, not `warn`. Beta passes `var(--red)` here — the same ink
              as the hero. Orange is its "still active" hue, and using it for a
              cost made a fee read as a caution rather than as money gone. */}
          <Cell
            label="Fees paid" value={moneyPlain(e.fees, 0)} cls="red"
            sub={`Eval ${moneyPlain(e.feeBreakdown.evalFees, 0)} · `
              + `Reset ${moneyPlain(e.feeBreakdown.reset, 0)} · `
              + `Activation ${moneyPlain(e.feeBreakdown.activation, 0)}`}
          />
          {/* ⚠ GOLD BELOW $1, green at or above it — beta's split, and the
              threshold is the whole point: a dollar back per dollar spent is
              break-even, so anything under it is losing money on the business
              of getting funded. Rendered in default ink it reads as neutral
              information rather than as the warning it is. */}
          <Cell
            label="Return on investment"
            // ⚠ `—` when nothing has been spent, never `$0.00`. "No data" and
            // "you got nothing back" are different statements, and the second is
            // an accusation.
            value={e.roiPerDollar == null ? '—' : `$${num(e.roiPerDollar, 2)}`}
            cls={e.roiPerDollar == null ? '' : e.roiPerDollar >= 1 ? 'green' : 'gold'}
            // ⚠ AND THE SUB-LABEL HAS TO MOVE WITH THE VALUE, which is the half
            // that shipped wrong. Beta reads `roiPerDollar == null ? 'no spend
            // logged yet' : 'per dollar spent'` (app/index.html:41295-41297).
            // A dash over the unconditional "per dollar spent" reads as a number
            // that failed to load; the tile has to say the question has no answer
            // yet, not that the answer is missing.
            sub={e.roiPerDollar == null ? 'no spend logged yet' : 'per dollar spent'}
          />
          {/* ⚠ THE LABEL FLIPS WITH THE SIGN, as in beta: a positive net is
              "Clear of break-even by" in green, a negative one is "Break-even
              needs" in gold. One tile, two statements — showing the deficit
              wording to someone who is up would read as a debt they do not owe.

              ⚠ THE NUMBER DID NOT CHANGE. `breakEvenGap` is
              `Math.max(0, fees − payouts)`, which IS `|netCash|` whenever the
              net is negative — the only case this tile had until now. `netCash`
              covers the surplus branch too, which `breakEvenGap` clamps to 0. */}
          <Cell
            label={e.netCash >= 0 ? 'Clear of break-even by' : 'Break-even needs'}
            value={moneyPlain(Math.abs(e.netCash), 0)}
            cls={e.netCash >= 0 ? 'green' : 'gold'}
            sub={e.netCash >= 0 ? 'in payouts' : 'more in payouts'}
          />
        </div>

        {/* ⚠ OUTSIDE every cash total, and the sentence says so. Folding trade
            P&L into the ledger is what made beta's old Capital Efficiency card
            read better than reality. */}
        <div className="pr-aside">
          <b className={e.simPnl >= 0 ? 'green' : 'red'}>{money(e.simPnl, 0)}</b>
          <span>
            <b>Sim trade P&amp;L</b> — shown for context and deliberately excluded above.
            It is account balance, not money you can spend.
          </span>
        </div>
      </section>

      <section className="card pr-card">
        <h3>Eval Pipeline</h3>
        <p className="muted">
          What it actually costs to reach a funded account, including the attempts that
          failed. Pass rate counts <b>accounts</b>, not attempts — resets belong to the
          eval they are on, so five resets on one eval is still one eval.
        </p>
        <div className="pr-cells pr-cells-5">
          <Cell
            label="Evals started" value={String(p.started)} big
            sub={p.freeAccounts ? `${p.freeAccounts} free` : 'none free'}
          />
          <Cell label="Still active" value={String(p.active)} cls="warn" big sub="" />
          <Cell
            label="Passed" value={String(p.passed)} cls="green" big
            // ⚠ Beta prints the pass rate under PASSED, not under EVALS STARTED —
            // it is a property of the outcome, and moving it changes which number
            // the reader thinks it divides.
            sub={p.passRate == null ? '' : `${pct(p.passRate, 0)} pass rate`}
          />
          <Cell label="Failed" value={String(p.failed)} cls="red" big sub="" />
          <Cell label="Funded" value={String(p.funded)} cls="green" big sub="" />
        </div>
        <div className="pr-cells pr-cells-mt">
          <Cell
            label="Cost per funded account"
            value={p.costPerFunded == null ? '—' : moneyPlain(p.costPerFunded, 0)}
            sub="all spend ÷ funded accounts"
          />
          <Cell
            label="Evals per funded"
            value={p.evalsPerFunded == null ? '—' : num(p.evalsPerFunded, 1)}
            sub="attempts on average"
          />
          <Cell
            label="Resets used" value={String(e.resets.count)}
            sub={`${e.resets.free} free · ${moneyPlain(e.resets.spend, 0)} paid · `
              + `${p.resetsPerPass == null ? '—' : num(p.resetsPerPass, 1)} per pass`}
          />
          <Cell
            label="Recurring burn"
            value={e.recurring.count ? `${moneyPlain(e.recurring.monthly, 0)}/mo` : '—'}
            sub={`${e.recurring.count} active subscription${e.recurring.count === 1 ? '' : 's'}`}
          />
        </div>
      </section>

      {/* ⚠ CONDITIONAL, as in beta. An empty rebill list means nothing is at
          risk, and a card saying so would be noise on a screen about money. */}
      {rebill.length ? <RebillCard rows={rebill} /> : null}

      {/* ⚠ CONDITIONAL TOO, AND FOR THE SAME REASON THE REBILL CARD ABOVE IS —
          this one shipped unconditional. Beta composes the card only through
          `const firmTable = firms.length < 2 ? '' : …` (app/index.html:41355),
          because a comparison needs two things to compare: with one firm the
          table restates the tiles above it, and with none it is a row of headers
          over nothing. The `<tbody>` was already data-driven, which is exactly
          why this was invisible — the table always had the right ROWS. */}
      {firms.length >= 2 ? (
        <section className="card pr-card">
          <h3>Firm Comparison</h3>
          <p className="muted">
            Which firms have actually paid you, and what each cost to get there.
          </p>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Firm</th>
                  <th className="num">Spend</th>
                  <th className="num">Payouts</th>
                  <th className="num">Net</th>
                  <th className="num">Evals</th>
                  <th className="num">Pass rate</th>
                  <th className="num">Funded</th>
                </tr>
              </thead>
              <tbody>
                {firms.map((f) => <FirmRowView key={f.firm} f={f} />)}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <VelocityCard v={velocity} />
    </>
  );
}

/**
 * Payout Velocity — the only forward-looking card on a tab of sunk costs.
 *
 * ⚠ IT RENDERS EVEN WHEN IT IS ENTIRELY EMPTY, which is the opposite of the call
 * made for Rebill Risk two cards up. The difference is what the emptiness MEANS.
 * An empty rebill list means nothing is at risk — good news, and a card saying
 * so is noise. An empty payout list means **no money has ever come back**, on a
 * tab whose other four cards are all costs. Hiding it would quietly remove the
 * one card that says the operation has never paid out.
 */
function VelocityCard({ v }: { v: PayoutVelocity }) {
  return (
    <section className="card pr-card">
      <h3>Payout Velocity</h3>
      <div className="pr-cells">
        <Cell
          label="YTD payouts"
          // ⚠ `$0` here, not `—`. Zero received IS the reading, and it is the
          // headline of the card. The AVERAGE below is the one that dashes,
          // because there is no average of nothing.
          value={moneyPlain(v.ytdTotal, 0)} cls="blue"
          sub={`${v.ytdCount} payout${v.ytdCount === 1 ? '' : 's'} · `
            + `avg ${v.avgPayout == null ? '—' : moneyPlain(v.avgPayout, 0)}`}
        />
        <Cell
          label="Cadence"
          value={v.cadenceDays == null ? '—' : `${num(v.cadenceDays, 0)} days`}
          sub="Avg between recent payouts"
        />
        <Cell
          label="Projected 30-day"
          value={v.projected30 == null ? '—' : moneyPlain(v.projected30, 0)}
          sub="At current pace"
        />
        <Cell
          label="Payout : Reset"
          // ⚠ ORANGE UNDER 2x, beta's threshold: being paid less than twice what
          // resets cost means the resets are eating the payouts. Above it the
          // figure is unremarkable and carries no colour.
          value={v.payoutToReset == null ? '—' : `${num(v.payoutToReset, 1)}×`}
          cls={v.payoutToReset != null && v.payoutToReset < 2 ? 'warn' : ''}
          sub={v.resetSpendYtd > 0
            ? `Spent ${moneyPlain(v.resetSpendYtd, 0)} on resets YTD`
            : 'No reset fees logged YTD'}
        />
      </div>

      {v.all.length ? (
        <div className="pr-velocity-spark">
          <div className="pr-cell-k">Cumulative payouts (all time)</div>
          <Sparkline points={v.all} />
        </div>
      ) : (
        <p className="muted pr-velocity-empty">
          No payouts logged yet. Add a “Payout” adjustment on any funded account to
          populate this section.
        </p>
      )}
    </section>
  );
}

/**
 * The cumulative-payout sparkline.
 *
 * ⚠ CUMULATIVE, not per-payout — the line only ever goes up, and its SLOPE is
 * the message. A per-payout chart of four similar payments is a flat line that
 * says nothing about whether the operation is accelerating.
 *
 * ⚠ Josh has no payouts, so this branch has never been seen on real data. It is
 * covered by a test with a fixture, which is weaker evidence than a screenshot
 * and is being written down as such.
 */
function Sparkline({ points }: { points: readonly { date: string; amount: number }[] }) {
  const W = 600;
  const H = 48;
  let running = 0;
  const cum = points.map((p) => (running += p.amount));
  const max = Math.max(...cum, 1);
  // ⚠ A single payout has no line to draw — one point is a dot, and dividing by
  // `length - 1` would be a divide-by-zero.
  const step = cum.length > 1 ? W / (cum.length - 1) : 0;
  const d = cum
    .map((y, i) => `${i === 0 ? 'M' : 'L'} ${(i * step).toFixed(1)} ${(H - (y / max) * H).toFixed(1)}`)
    .join(' ');

  return (
    <svg
      className="pr-spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
      role="img" aria-label={`Cumulative payouts across ${points.length} payments`}
    >
      <path d={d} fill="none" stroke="var(--blue)" strokeWidth="2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function FirmRowView({ f }: { f: FirmRow }) {
  return (
    <tr>
      <td>{f.firm}</td>
      {/* ⚠ Spend and payouts are MAGNITUDES — a cost and a receipt, never signed.
          Only `net` is a result, and it is the only cell that carries a sign.
          Beta signs all three, so its table shows "+$76" of money you spent. */}
      <td className="num">{moneyPlain(f.spend, 0)}</td>
      <td className="num">{f.payouts ? moneyPlain(f.payouts, 0) : '—'}</td>
      <td className={`num ${f.net > 0 ? 'green' : f.net < 0 ? 'red' : ''}`}>
        {money(f.net, 0)}
      </td>
      <td className="num">{f.evals}</td>
      {/* ⚠ `—` for a firm with no evals, never `0%`. A firm you went straight to
          funded with has no pass rate, and 0% would read as "you failed them all". */}
      <td className="num">{f.passRate == null ? '—' : pct(f.passRate, 0)}</td>
      <td className="num">{f.funded}</td>
    </tr>
  );
}

function RebillCard({ rows }: { rows: RebillRisk[] }) {
  return (
    <section className="card pr-card">
      <h3>Rebill Risk</h3>
      <p className="muted">
        Recurring accounts you have not traded in a fortnight. The rebill date is
        <b> estimated</b> — the last eval fee plus whole months — because firms do not
        publish their billing day.
      </p>
      {rows.map((r) => (
        <div className="pr-rebill" key={r.accountId}>
          <span className="pr-rebill-name">{r.name}</span>
          <span className="muted">{r.firm}</span>
          <span className="muted">idle {r.idleDays} days</span>
          <span className="grow" />
          <b className={r.daysToRebill != null && r.daysToRebill <= 7 ? 'warn' : ''}>
            {r.daysToRebill == null
              ? `${moneyPlain(r.amount, 0)}/mo`
              : `${moneyPlain(r.amount, 0)} in ~${r.daysToRebill} days`}
          </b>
        </div>
      ))}
    </section>
  );
}

function Cell(
  { label, value, sub, cls, big }:
  { label: string; value: string; sub: string; cls?: string; big?: boolean },
) {
  // 🚨 `big` PUTS THE NUMBER ABOVE THE LABEL, AND THAT IS NOT DECORATION.
  // Beta has two tile helpers on this tab and they are ordered differently:
  // `tile()` renders label-then-value, `step()` renders VALUE-then-label. The
  // five Eval Pipeline steps are `step()`s; everything else is a `tile()`.
  //
  // This component rendered every one of them label-first, so the pipeline read
  // as five captioned figures instead of five big counts — the shape a funnel
  // is supposed to have. Caught by Josh's full-resolution screenshot, and by
  // nothing else: the DOM carries both orders and jsdom applies no CSS, so
  // every test passed either way.
  return (
    <div className={`pr-cell ${big ? 'pr-step' : ''}`}>
      {big ? null : <div className="pr-cell-k">{label}</div>}
      <div className={`pr-cell-v ${big ? 'big' : ''} ${cls ?? ''}`}>{value}</div>
      {big ? <div className="pr-cell-k">{label}</div> : null}
      {sub ? <div className="muted pr-cell-s">{sub}</div> : null}
    </div>
  );
}
